/**
 * ProductItemEditModal 컴포넌트
 *
 * 상품 아이템 수정 모달
 * - 단일 유형(SINGLE): 연결 강의 변경 + 할인가 수정
 * - 로드맵 유형(ROADMAP): 할인가만 수정
 */

import { CourseResponseDto } from '@repo/types';
import { PRODUCT_KIND, PRODUCT_PACKAGE_KIND } from '@theailab-dev/parchment';
import type { ProductKind, ProductPackageKind } from '@theailab-dev/parchment';
import React, { useState } from 'react';
import { HiX } from 'react-icons/hi';
import { Button } from './Button';
import ConfirmModal from './ConfirmModal';
import CourseSelectModal from './CourseSelectModal';
import { ProductItemDto } from '../../api/productApi';
import { useProductItemForm } from '../../hooks/useProductItemForm';

// 할인가 입력 컴포넌트 (천단위 콤마 지원)
const DiscountInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}> = ({ value, onChange, disabled, placeholder }) => {
  const [isFocused, setIsFocused] = useState(false);

  // 숫자만 추출하여 천단위 콤마 적용
  const formatValue = (val: string): string => {
    if (!val || val === '0') return '';
    const numericValue = val.replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    return parseInt(numericValue, 10).toLocaleString();
  };

  // 표시할 값 결정
  const displayValue = isFocused ? (value === '0' ? '' : value.replace(/,/g, '')) : formatValue(value);

  return (
    <input
      type="text"
      value={displayValue}
      onChange={(e) => {
        // 숫자만 추출
        const numericValue = e.target.value.replace(/[^0-9]/g, '');
        // 앞의 0 제거 (단, 빈 값은 허용)
        const cleanedValue = numericValue.replace(/^0+/, '') || '0';
        onChange(cleanedValue);
      }}
      onFocus={() => {
        setIsFocused(true);
        // 포커스 시 0이면 빈 문자열로 변경
        if (value === '0') {
          onChange('');
        }
      }}
      onBlur={() => {
        setIsFocused(false);
        // 포커스 해제 시 빈 값이면 0으로 설정 (하지만 표시는 안 함)
        if (!value || value === '') {
          onChange('0');
        }
      }}
      disabled={disabled}
      placeholder={placeholder || '0'}
      className="block w-full rounded-lg border border-slate-200 px-3 py-2.5 text-right shadow-sm transition-colors duration-200 focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 sm:text-sm"
    />
  );
};

interface ProductItemEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    itemId: number,
    data: {
      courseId?: number;
      discount: number;
      packageKind?: ProductPackageKind;
    },
  ) => Promise<void>;
  item: ProductItemDto | null;
  productKind: ProductKind; // 상품 유형
  existingItems?: ProductItemDto[]; // 현재 상품에 등록된 아이템 목록 (REQUIREMENT 개수 확인용)
}

const ProductItemEditModal: React.FC<ProductItemEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  item,
  productKind,
  existingItems = [],
}) => {
  const [showCourseSelectModal, setShowCourseSelectModal] = useState(false);

  // 폼 로직 훅 사용
  // - initialItem을 전달하여 수정 모드로 초기화
  // - onSubmit: 수정 API 호출
  const {
    discount,
    setDiscount,
    selectedCourse,
    packageKind,
    setPackageKind,
    isSubmitting,
    alertModal,
    showAlert,
    closeAlert,
    handleCourseSelect,
    validateAndSubmit,
    originalPrice,
  } = useProductItemForm({
    initialItem: item,
    productKind,
    existingItems,
    isOpen,
    onClose,
    onSubmit: async (data) => {
      if (!item) return;
      const saveData = {
        // 단일 상품이고 강의가 변경된 경우에만 courseId 포함
        ...(data.courseId ? { courseId: data.courseId } : {}),
        discount: data.discount,
        packageKind: data.packageKind,
      };
      console.log('📝 ProductItemEditModal onSubmit:', {
        itemId: item.id,
        saveData,
      });
      await onSave(item.id, saveData);
    },
  });

  // 강의 선택 핸들러 (단일 상품 전용)
  // - 훅의 handleCourseSelect 호출 후 모달 닫기
  const onCourseSelect = (course: CourseResponseDto) => {
    handleCourseSelect(course);
    setShowCourseSelectModal(false);
  };

  if (!isOpen || !item) return null;

  // 렌더링용 원가 계산
  const displayOriginalPrice =
    productKind === PRODUCT_KIND.SINGLE && selectedCourse && selectedCourse.price
      ? selectedCourse.price
      : (originalPrice ?? item.originalPrice);

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="text-xl font-semibold text-slate-800">상품 아이템 수정</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg p-1 transition-colors hover:bg-slate-100 disabled:opacity-50">
            <HiX className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* 내용 */}
        <div className="space-y-4 p-6">
          {/* 연결 강의 (단일 유형만 변경 가능) */}
          {productKind === PRODUCT_KIND.SINGLE && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">연결 강의</label>
              <div className="space-y-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  {selectedCourse?.title || item.course?.title || '제목 없음'}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCourseSelectModal(true)}
                  disabled={isSubmitting}
                  className="w-full">
                  변경
                </Button>
              </div>
            </div>
          )}

          {/* 원가 */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">원가 (원)</label>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              {displayOriginalPrice.toLocaleString()}원
            </div>
          </div>

          {/* 할인가 */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">할인가 (원)</label>
            <DiscountInput value={discount} onChange={setDiscount} disabled={isSubmitting} placeholder="0" />
            {discount && discount !== '0' && (
              <p className="text-right text-xs text-slate-500">
                최종 가격:{' '}
                <span className="font-semibold text-slate-900">
                  {(displayOriginalPrice - (parseFloat(discount.replace(/,/g, '')) || 0)).toLocaleString()}원
                </span>
              </p>
            )}
          </div>

          {/* 패키지 유형 선택 */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">
              패키지 유형 <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={packageKind}
              onChange={(e) => {
                const newKind = e.target.value as ProductPackageKind;
                // REQUIREMENT는 최소 1개 필수 검증
                // packageKind가 없으면 기본값으로 REQUIREMENT로 간주
                const otherItems = existingItems.filter((i) => i.id !== item.id);
                const otherRequirementCount = otherItems.filter(
                  (i) => i.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !i.packageKind,
                ).length;

                if (newKind === PRODUCT_PACKAGE_KIND.OPTION && otherRequirementCount === 0) {
                  showAlert(
                    'error',
                    '선택 불가',
                    '필수(REQUIREMENT) 아이템이 최소 1개 이상 있어야 합니다.\n현재 다른 필수 아이템이 없으므로 이 아이템은 필수로 설정해야 합니다.',
                  );
                  return;
                }
                setPackageKind(newKind);
              }}
              disabled={isSubmitting}>
              <option value={PRODUCT_PACKAGE_KIND.REQUIREMENT}>필수 (REQUIREMENT)</option>
              <option value={PRODUCT_PACKAGE_KIND.OPTION}>선택 (OPTION)</option>
            </select>
            <p className="text-xs text-slate-500">
              {existingItems.filter(
                (i) => i.id !== item.id && (i.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !i.packageKind),
              ).length === 0
                ? '⚠️ 다른 필수 아이템이 없습니다. 이 아이템은 필수로 설정해야 합니다.'
                : `현재 다른 필수 아이템: ${existingItems.filter((i) => i.id !== item.id && (i.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !i.packageKind)).length}개`}
            </p>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button variant="primary" onClick={validateAndSubmit} disabled={isSubmitting}>
            {isSubmitting ? '저장 중...' : '저장'}
          </Button>
        </div>
      </div>

      {/* 알림 모달 */}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      {/* 강의 선택 모달 (단일 유형만) */}
      <CourseSelectModal
        isOpen={showCourseSelectModal}
        onClose={() => setShowCourseSelectModal(false)}
        onSelect={onCourseSelect}
      />
    </div>
  );
};

export default ProductItemEditModal;
